import type { Schema } from "@/api/types";

export type Chain = Schema<"OptionChainOut">;
export type ChainRow = Schema<"ChainRow">;
export type Leg = Schema<"ChainLeg">;
export type Underlying = Schema<"UnderlyingOut">;

/**
 * Where the spot line goes: the index of the first strike above the spot, so
 * the line sits between that row and the one before (rows.length = below all).
 * Null without a spot.
 */
export function spotLineIndex(rows: readonly ChainRow[], spot: number | null | undefined): number | null {
  if (spot == null || rows.length === 0) return null;
  const i = rows.findIndex((r) => r.strike > spot);
  return i === -1 ? rows.length : i;
}

/** In the money: calls below the spot, puts above it. */
export function isItm(strike: number, spot: number | null | undefined, side: "call" | "put"): boolean {
  if (spot == null) return false;
  return side === "call" ? strike < spot : strike > spot;
}

/** The largest OI on each side, for the OI bars. */
export function maxOi(rows: readonly ChainRow[]): { call: number; put: number } {
  let call = 0;
  let put = 0;
  for (const r of rows) {
    call = Math.max(call, r.call?.oi ?? 0);
    put = Math.max(put, r.put?.oi ?? 0);
  }
  return { call, put };
}

/** `n` strikes each side of the ATM strike (all rows for "all" or without an ATM). */
export function aroundAtm(rows: readonly ChainRow[], atm: number | null | undefined, range: string): ChainRow[] {
  const n = Number(range);
  if (!Number.isFinite(n) || atm == null || rows.length === 0) return [...rows];
  let i = 0;
  rows.forEach((r, j) => {
    if (Math.abs(r.strike - atm) < Math.abs((rows[i]?.strike ?? r.strike) - atm)) i = j;
  });
  return rows.slice(Math.max(0, i - n), i + n + 1);
}

/** Strike text: no decimals unless the strike has them (stock strikes like 1,182.5). */
export function strikeText(k: number): string {
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(k);
}
